import {
  Center,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Thead,
  Text,
} from '@chakra-ui/react'
import { QueryDocumentSnapshot } from 'firebase/firestore'
import { useMemo, forwardRef, useImperativeHandle, Ref } from 'react'
import FirestoreUser from '../types/FirestoreUser'
import useSortableTable from '../hooks/useSortableTable'
import { createSortComparator } from '../utils/sortUtils'
import TableHeader from './TableHeader'
import UserTableRow from './UserTableRow'
import { ReportInfo } from '../types/ReportTypes'

interface UsersTableProps {
  docs: QueryDocumentSnapshot<FirestoreUser>[]
  lastNameFirst?: boolean
  reports?: Record<string, ReportInfo>
  isLoading?: boolean
  emailFirst?: boolean // If true, show email as first column, then name
}

export interface UsersTableRef {
  scrollToInitial: (
    initial: string,
    field: 'firstName' | 'lastName' | 'email'
  ) => boolean
}

/**
 * Table component displaying user data with sortable columns
 */
const UsersTable = forwardRef(function UsersTable(
  {
    docs,
    lastNameFirst = false,
    reports = {},
    isLoading = false,
    emailFirst = false,
  }: UsersTableProps,
  ref: Ref<UsersTableRef>
) {
  // Use the custom hook for sorting
  // If emailFirst, always sort by email ascending
  const { sortField, sortDirection, sortingField, handleSort } =
    useSortableTable('name', 'asc')

  const sortedDocs = useMemo(() => {
    if (emailFirst) {
      return [...docs].sort((a, b) => {
        const ea = (a.data().email || '').toLowerCase()
        const eb = (b.data().email || '').toLowerCase()
        return ea.localeCompare(eb)
      })
    }
    if (!sortField) return docs
    return [...docs].sort(
      createSortComparator(sortField, sortDirection, reports, lastNameFirst)
    )
  }, [docs, sortField, sortDirection, reports, lastNameFirst, emailFirst])

  // Expose scrollToInitial via ref
  useImperativeHandle(
    ref,
    () => ({
      scrollToInitial: (
        initial: string,
        field: 'firstName' | 'lastName' | 'email'
      ): boolean => {
        if (!sortedDocs.length) return false
        const upperInitial = initial.toUpperCase()
        let foundIdx = -1
        let nextCharCode = upperInitial.charCodeAt(0)
        while (foundIdx === -1 && nextCharCode <= 90) {
          const letter = String.fromCharCode(nextCharCode)
          foundIdx = sortedDocs.findIndex((doc) => {
            if (field === 'email') {
              const email = (doc.data().email || '') as string
              return email && email[0].toUpperCase() === letter
            } else {
              const profile = doc.data().profile
              if (!profile) return false
              const name =
                field === 'firstName' ? profile.firstName : profile.lastName
              return name && name[0].toUpperCase() === letter
            }
          })
          nextCharCode++
        }
        if (foundIdx !== -1) {
          const row = document.getElementById(
            `user-row-${sortedDocs[foundIdx].id}`
          )
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return true
          }
        }
        return false
      },
    }),
    [sortedDocs]
  )

  // Render loading spinner when loading
  if (isLoading) {
    return (
      <TableContainer>
        <Center py={8}>
          <Spinner size='xl' thickness='4px' color='blue.500' />
          <Text ml={4} fontSize='lg'>
            Loading user data...
          </Text>
        </Center>
      </TableContainer>
    )
  }

  return (
    <TableContainer>
      <Table size='sm'>
        <Thead>
          <TableHeader
            sortField={sortField}
            sortDirection={sortDirection}
            sortingField={sortingField}
            onSort={handleSort}
            emailFirst={emailFirst}
          />
        </Thead>
        <Tbody>
          {sortedDocs.map((doc) => (
            <UserTableRow
              key={doc.id}
              doc={doc}
              lastNameFirst={lastNameFirst}
              reports={reports}
              rowId={`user-row-${doc.id}`}
              emailFirst={emailFirst}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
})

export default UsersTable
